import { useCallback, useMemo } from 'react';
import {
  CalendarIcon,
  CoinIcon,
  HouseIcon,
  ListUlIcon,
  PersonGearIcon
} from '@ghq-abi/design-system-icons';
import { useTranslate } from '@tolgee/react';

import { useAbility } from '~/core/contexts/AbilityContext';
import { useFlags } from '~/core/contexts/FlagsContext';
import { RouteObject } from '~/core/types';
import { PagesPathsKeys } from '~/shared/constants/pages';

export function useMenuRoutes() {
  const { t } = useTranslate();
  const ability = useAbility();
  const flags = useFlags();

  const hasAuthorizedFlags = useCallback(
    (authorizedFlags: PagesPathsKeys | PagesPathsKeys[]) => {
      if (
        typeof authorizedFlags === 'string' &&
        !flags[authorizedFlags].active
      ) {
        return false;
      }
      if (
        Array.isArray(authorizedFlags) &&
        !authorizedFlags.some(flag => flags[flag].active)
      ) {
        return false;
      }
      return true;
    },
    [flags]
  );

  const { menuRoutes, footerRoutes } = useMemo(() => {
    const menuRoutesList: RouteObject[] = [];
    const footerRoutesList: RouteObject[] = [];

    // Common access for every user
    menuRoutesList.push({
      key: 'home',
      title: t('menu.home'),
      icon: <HouseIcon color="#191F2E" />,
      path: '/home'
    });

    if (flags.home?.active && ability.can('view', 'Home')) {
      // Should not be accessed by obsolete plan users
      menuRoutesList.push({
        key: 'my-plan',
        title: t('menu.iapp'),
        icon: <CoinIcon color="#191F2E" />,
        path: '/my-plan'
      });
    }

    // This verification is done regardless of the plan status (obsolete or not)
    // User only need to be global admin to access this set of menu items
    if (ability.can('view', 'Administration')) {
      menuRoutesList.push(
        {
          key: 'campaigns',
          title: t('menu.campaigns'),
          icon: <CalendarIcon color="#191F2E" />,
          path: '/campaigns'
        },
        {
          key: 'requests',
          title: t('menu.requests'),
          icon: <ListUlIcon color="#191F2E" />,
          path: '/requests'
        },
        {
          key: 'admin',
          title: t('menu.admin'),
          icon: <PersonGearIcon color="#191F2E" />,
          path: '/admin'
        }
      );
    }

    return { menuRoutes: menuRoutesList, footerRoutes: footerRoutesList };
  }, [ability, t, flags]);

  return { menuRoutes, footerRoutes, hasAuthorizedFlags };
}
